import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Fade, Grid, LinearProgress, Skeleton } from '@mui/material';
import CodebookPanel from '../coding/CodebookPanel';
import { NotFound } from '../layout/SystemState';
import { getCodebook, getNext } from '../../services/adjudicationService';
import ContextCard, { ContextSkeleton } from './ContextCard';
import BlindStep from './BlindStep';
import RevealStep, { RevealCompare } from './RevealStep';
import TieStep, { TieBand, TieCodes } from './TieStep';
import { ItemHeader, Panel, lockedLabel } from './parts';

const firstDiffering = (view) => ['ut', 'rc'].find((d) => view.reasonsNeeded?.[d]) || (view.tiedDimensions || [])[0];

// /adjudicate/:runId (YES-256–258): always the run's next item for the caller, at the step where
// they stopped. The URL carries the run only; no item, position or lesson ID.
const AdjudicationItem = () => {
    const { runId } = useParams();
    const [view, setView] = useState(null);
    const [status, setStatus] = useState('loading');
    const [codebook, setCodebook] = useState(null);
    const [codebookFailed, setCodebookFailed] = useState(false);
    const [codebookTab, setCodebookTab] = useState('ut');
    const [highlight, setHighlight] = useState({});
    const [wholeLesson, setWholeLesson] = useState(false);
    const [notice, setNotice] = useState(null);

    const load = useCallback(async () => {
        setStatus((s) => (s === 'ready' ? 'advancing' : 'loading'));
        try {
            const next = await getNext(runId);
            setView(next);
            setWholeLesson(false);
            setHighlight({});
            setStatus('ready');
        } catch (err) {
            setStatus(err.response?.status === 404 ? 'notFound' : 'error');
        }
    }, [runId]);

    const loadCodebook = useCallback(async () => {
        setCodebookFailed(false);
        try {
            setCodebook(await getCodebook(runId));
        } catch (err) {
            setCodebookFailed(true);
        }
    }, [runId]);

    useEffect(() => {
        load();
        loadCodebook();
    }, [load, loadCodebook]);

    // The codebook panel follows the step: the dimension that differs or is tied after the lock
    useEffect(() => {
        if (view?.step === 'reveal' || view?.step === 'tie') setCodebookTab(firstDiffering(view) || 'ut');
        if (view?.step === 'blind') setCodebookTab('ut');
    }, [view]);

    // The lock answers with the reveal only; the run, progress and window stay
    const onReveal = (reveal) => {
        setView((current) => ({ ...current, ...reveal }));
        setHighlight({ ut: [reveal.yours.ut, ...reveal.earlier.map((e) => e.ut)], rc: [reveal.yours.rc, ...reveal.earlier.map((e) => e.rc)] });
    };

    const onMovedOn = () => {
        setNotice('This item has already moved on. Here is your next one.');
        load();
    };

    const onDone = () => {
        setNotice(null);
        load();
    };

    if (status === 'notFound') return <NotFound />;
    if (view?.step === 'done') return <Navigate to="/adjudicate" replace />;

    if (status === 'error' && !view) {
        return (
            <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6 }}>
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>
                    Couldn’t load your next item.
                </Alert>
            </Box>
        );
    }

    if (!view) {
        return (
            <Box aria-busy="true">
                <Skeleton width={180} height={20} />
                <Skeleton width={280} height={36} sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}><ContextSkeleton /></Grid>
                    <Grid item xs={12} md={4}><Panel title="Your code"><Skeleton height={160} /></Panel></Grid>
                </Grid>
            </Box>
        );
    }

    const steps = {
        blind: <BlindStep runId={runId} view={view} codebook={codebook} onPicksChange={setHighlight} onLocked={onReveal} onMovedOn={onMovedOn} />,
        reveal: (
            <Box sx={{ display: 'grid', gap: 2 }}>
                <RevealCompare view={view} />
                <RevealStep runId={runId} view={view} onDone={onDone} onMovedOn={onMovedOn} />
            </Box>
        ),
        tie: (
            <Box sx={{ display: 'grid', gap: 2 }}>
                <TieCodes view={view} />
                <TieStep runId={runId} view={view} codebook={codebook} onPicksChange={setHighlight} onDone={onDone} onMovedOn={onMovedOn} />
            </Box>
        ),
    };

    const codebookReady = Boolean(codebook);
    const stepPanel = codebookReady || view.step === 'reveal'
        ? steps[view.step]
        : (
            <Panel title="Your code">
                {codebookFailed ? (
                    <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadCodebook}>Try again</Button>}>
                        Couldn’t load the codebook.
                    </Alert>
                ) : <Skeleton height={180} />}
            </Panel>
        );

    return (
        <Box sx={{ position: 'relative' }}>
            {status === 'advancing' && <LinearProgress sx={{ position: 'absolute', top: -12, left: 0, right: 0, height: 2 }} aria-label="Loading the next item" />}
            <ItemHeader run={view.run} progress={view.progress} />
            {view.step === 'tie' && <TieBand view={view} />}
            {view.step === 'reveal' && view.reopened && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    You locked this item on {lockedLabel(view.yours.lockedAt)}. Give the reason to finish it.
                </Alert>
            )}
            {notice && <Alert severity="warning" onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice}</Alert>}

            <Fade in key={`${view.item.id}-${view.step}`} timeout={200}>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={7} lg={8} sx={{ display: 'grid', gap: 2 }}>
                        <ContextCard runId={runId} item={view.item} coding={view.step === 'blind'} onWholeLessonChange={setWholeLesson} />
                        {/* Hidden while the whole lesson is open, to give the transcript room */}
                        {!wholeLesson && (codebookReady ? (
                            <CodebookPanel
                                inline
                                codebook={codebook}
                                speaker={view.item.speaker}
                                tab={codebookTab}
                                onTabChange={setCodebookTab}
                                highlight={highlight}
                            />
                        ) : codebookFailed ? (
                            <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadCodebook}>Try again</Button>}>
                                Couldn’t load the codebook.
                            </Alert>
                        ) : <Skeleton variant="rounded" height={200} />)}
                    </Grid>
                    <Grid item xs={12} md={5} lg={4} sx={{ position: { md: 'sticky' }, top: { md: 72 } }}>
                        {stepPanel}
                    </Grid>
                </Grid>
            </Fade>
        </Box>
    );
};

export default AdjudicationItem;
